import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { BarChart3, Settings } from "lucide-react";
import Spinner from "../../../../assets/icons/Spinner";
import TrashIcon from "../../../../assets/icons/TrashIcon";
import ChevronLeftIcon from "../../../../assets/icons/ChevronLeftIcon";
import Message from "../../../../i18n/Message";
import { deleteGateway, loadGateway } from "../store/gatewayActionCreators";
import { gatewaySelectors } from "../store/gatewayReducer";
import GatewayTitle from "./GatewayTitle";
import GatewaySettings from "./GatewaySettings";
import GatewayTab from "./gatewayTab";
import GatewayAnalyticsTab from "./analytics/GatewayAnalyticsTab";
import NewWorkspaceLayout from "../../layout/NewWorkspaceLayout";
import { linkToGateways } from "../../../../routes";

import "./GatewayDetailsPage.scss";

const GatewayDetailsPage = () => {
  const { gatewayId, workspaceId } = useParams();
  const intl = useIntl();
  const nav = useNavigate();
  const dispatch = useDispatch();
  const gateway = useSelector(gatewaySelectors.getGateway);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tab, setTab] = useState(GatewayTab.ANALYTICS);

  useEffect(() => {
    if (!gateway || gateway?.uid !== gatewayId) {
      dispatch(loadGateway(intl, workspaceId, gatewayId));
    }
  }, [gateway, gatewayId, workspaceId, dispatch, intl]);

  const onDeleteConfirmed = () => {
    const onSuccess = () => {
      setShowDeleteConfirm(false);
      nav(linkToGateways(workspaceId));
    };
    dispatch(deleteGateway(intl, workspaceId, gatewayId, onSuccess));
  };

  const item = !!gateway && gateway?.uid === gatewayId ? gateway : null;

  return (
    <NewWorkspaceLayout contentPanelClassName="gateway_details_main">
      {!item && (
        <div className="spinner_container">
          <Spinner />
        </div>
      )}
      {item && (
        <>
          <div className="title_row">
            <div className="title_column">
              <div className="breadcrumbs">
                <div onClick={() => nav(linkToGateways(workspaceId))}>
                  <ChevronLeftIcon
                    size={16}
                    strokeWidth="2"
                    color="rgb(103, 116, 142)"
                  />
                  <Message id="back_to_gateways" />
                </div>
              </div>
              <div className="title_block">
                <div className="title_left">
                  <GatewayTitle gateway={item} workspaceId={workspaceId} />
                  <div className="metadata_row">
                    <div className="meta_name">
                      <Message id="created_on" />
                    </div>
                    <div className="meta_value">
                      {dayjs(item.dateCreated).format("DD MMM")}
                    </div>
                  </div>
                </div>
                <div className="tabs_container">
                  <div className="tab_group">
                    <div
                      className={
                        "tab_group_item" +
                        (tab === GatewayTab.ANALYTICS ? " selected" : "")
                      }
                      onClick={() => setTab(GatewayTab.ANALYTICS)}
                    >
                      <BarChart3 size={18} color="#344767" />
                      <Message id="analytics" />
                    </div>
                    <div
                      className={
                        "tab_group_item" +
                        (tab === GatewayTab.SETTINGS ? " selected" : "")
                      }
                      onClick={() => setTab(GatewayTab.SETTINGS)}
                    >
                      <Settings size={18} color="#344767" />
                      <Message id="settings" />
                    </div>
                  </div>
                </div>
                <div className="title_actions">
                  <div className="confirm_container">
                    {showDeleteConfirm && (
                      <>
                        <div
                          className="cover"
                          onClick={() => setShowDeleteConfirm(false)}
                        ></div>
                        <div className="confirm_panel">
                          <button
                            className="btn bg-gradient-primary btn-sm"
                            onClick={onDeleteConfirmed}
                          >
                            <Message id="confirm_delete" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    className="btn no-btn tile-action"
                    onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
                  >
                    <TrashIcon size="16" color="#344767" />
                    <Message id="delete" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {tab === GatewayTab.ANALYTICS && (
            <GatewayAnalyticsTab gateway={item} workspaceId={workspaceId} />
          )}
          {tab === GatewayTab.SETTINGS && (
            <div className="card settings_panel">
              <GatewaySettings gateway={item} workspaceId={workspaceId} />
            </div>
          )}
        </>
      )}
    </NewWorkspaceLayout>
  );
};

export default GatewayDetailsPage;
